import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Avatar,
  CheckBox,
  CheckIcon,
  Drawer,
  SearchInput,
  SingleUserIcon,
  UserGroupIcon,
} from "components";
import { useGetAllResources } from "controller";
import { useState } from "react";
import { emptyStringHandler } from "utilz/helpers";
import { timeInSessionMs, trackAiHome } from "../../analytics";
import { useDashboardScope } from "../../hooks/useDashboardScope";
import type {
  DashboardScopeResource,
  DashboardScopeSelection,
} from "../../types";

/** Shape of a row returned by the resource list endpoint. */
interface ResourceRow {
  mapping_id: string;
  first_name?: string | null;
  last_name?: string | null;
  profile_image?: string | null;
}

/** Placeholder rows shown while the resource list loads. */
const SKELETON_ROWS = [1, 2, 3, 4, 5];

const toScopeResource = (resource: ResourceRow): DashboardScopeResource => ({
  id: resource.mapping_id,
  firstName: emptyStringHandler(resource.first_name),
  lastName: emptyStringHandler(resource.last_name),
  profileImage: resource.profile_image,
});

const getFullname = (resource: DashboardScopeResource) =>
  `${resource.firstName} ${resource.lastName}`.trim();

/** A selection that names nobody is not a valid scope — it means "Me". */
const toSelection = (
  resources: DashboardScopeResource[],
): DashboardScopeSelection =>
  resources.length ? { mode: "resources", resources } : { mode: "me" };

/**
 * "Me / Team" scope tabs shown beside the greeting. "Me" pins every analytics
 * widget to the signed-in resource; "Team" opens a picker and scopes them to one
 * or several team members, or — via Select all — to every one of them.
 *
 * "Select all" is a keyword on the wire, not an enumerated id list, so it covers
 * team members the picker never paged in.
 */
export const DashboardScopeFilter = () => {
  const theme = useTheme();
  const { mode, resources, setSelection } = useDashboardScope();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // The selection is drafted here and only committed on Apply — a multi-select
  // that refetched on every tick would fire a request per click.
  const [draft, setDraft] = useState<DashboardScopeSelection>({ mode: "me" });

  // Only fetched once the picker is opened — the tabs themselves don't need
  // the list.
  const { data, isFetching } = useGetAllResources({
    perPageCount: 1000,
    sortColumn: "users.first_name",
    ...(searchQuery && { searchQuery }),
    enabled: isDrawerOpen,
  });

  const resourceRows: ResourceRow[] = data?.result ?? [];

  const isMe = mode === "me";
  const isDraftAll = draft.mode === "all";
  const draftResources: DashboardScopeResource[] =
    draft.mode === "resources" ? draft.resources : [];

  const openDrawer = () => {
    // Reopen where the applied selection left off, not where the last draft did.
    setDraft(mode === "all" ? { mode: "all" } : toSelection(resources));
    setIsDrawerOpen(true);
  };

  const toggleResource = (resource: ResourceRow) => {
    const scope = toScopeResource(resource);
    // Coming out of "All", a single tick means "just this one" rather than
    // "everyone except" — the keyword carries no list to subtract from.
    const current = isDraftAll ? [] : draftResources;
    setDraft(
      toSelection(
        current.some((item) => item.id === scope.id)
          ? current.filter((item) => item.id !== scope.id)
          : [...current, scope],
      ),
    );
  };

  const handleApply = () => {
    // An empty selection would read as tenant-wide on the report endpoints and as
    // a 400 on the dashboard one, so it collapses back to "Me".
    const selection = isDraftAll
      ? ({ mode: "all" } as const)
      : toSelection(draftResources);

    if (isMe !== (selection.mode === "me")) {
      trackAiHome("home_me_team_toggled", {
        direction: selection.mode === "me" ? "team_to_me" : "me_to_team",
        scope_mode: selection.mode,
        resource_count:
          selection.mode === "resources" ? selection.resources.length : 0,
        time_in_session_ms: timeInSessionMs(),
      });
    }

    setSelection(selection);
    setIsDrawerOpen(false);
  };

  const handleSelectMe = () => {
    if (!isMe) {
      trackAiHome("home_me_team_toggled", {
        direction: "team_to_me",
        scope_mode: "me",
        resource_count: 0,
        time_in_session_ms: timeInSessionMs(),
      });
    }
    setSelection({ mode: "me" });
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          alignSelf: "center",
          gap: "2px",
          p: "2px",
          borderRadius: "8px",
          backgroundColor: theme.palette.neutral["005"],
        }}
      >
        <ScopeTab
          label="Me"
          icon={<SingleUserIcon sx={{ fontSize: "16px" }} />}
          isActive={isMe}
          onClick={handleSelectMe}
        />
        <ScopeTab
          label="Team"
          icon={<UserGroupIcon sx={{ fontSize: "16px" }} />}
          isActive={!isMe}
          onClick={openDrawer}
        />
      </Stack>

      <Drawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Team members"
        primaryButtonText="Apply"
        onPrimaryButtonClick={handleApply}
        secondaryButtonText="Clear all"
        onSecondaryButtonClick={() => setDraft({ mode: "me" })}
        headerStyle={{ borderBottom: "none" }}
        // Fixed width — without it the drawer grows to fit the longest name.
        drawerStyle={{ width: "372px" }}
        additionalHeaderComponent={
          <Stack
            sx={{
              gap: "8px",
              px: "24px",
              pt: "16px",
              pb: "0px",
              borderTop: `1px solid ${theme.palette.neutral["015"]}`,
            }}
          >
            <SearchInput
              placeholder="Search"
              name="search"
              fullWidth
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClear={() => setSearchQuery("")}
            />
            <Stack
              direction="row"
              sx={{
                mt: "8px",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack
                component="label"
                direction="row"
                sx={{ alignItems: "center", gap: "8px", cursor: "pointer" }}
              >
                <CheckBox
                  checked={isDraftAll}
                  inputProps={{ "aria-label": "Select the whole team" }}
                  onChange={() =>
                    setDraft(isDraftAll ? { mode: "me" } : { mode: "all" })
                  }
                />
                <Typography
                  variant="button-label"
                  sx={{ color: theme.palette.lightBg.high }}
                >
                  Select all
                </Typography>
              </Stack>
              <Typography
                component="button"
                type="button"
                variant="button-small"
                aria-label="Clear the team selection"
                onClick={() => setDraft({ mode: "me" })}
                sx={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  p: 0,
                  color: theme.palette.purple.main,
                }}
              >
                Clear all
              </Typography>
            </Stack>
          </Stack>
        }
      >
        {isFetching && resourceRows.length === 0 ? (
          <Stack sx={{ gap: "12px", p: "16px 24px" }}>
            {SKELETON_ROWS.map((row) => (
              <Stack
                key={row}
                direction="row"
                sx={{ alignItems: "center", gap: "12px" }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="text" sx={{ flex: 1 }} />
              </Stack>
            ))}
          </Stack>
        ) : resourceRows.length === 0 ? (
          <Stack sx={{ alignItems: "center", p: "32px 24px" }}>
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No team members found
            </Typography>
          </Stack>
        ) : (
          <List sx={{ width: "100%" }}>
            {resourceRows.map((resource) => {
              // Under "All" every row reads as selected, because it is.
              const isSelected =
                isDraftAll ||
                draftResources.some((item) => item.id === resource.mapping_id);
              const fullname = getFullname(toScopeResource(resource));

              return (
                <ListItem
                  key={resource.mapping_id}
                  disablePadding
                  secondaryAction={
                    isSelected ? (
                      <Box
                        sx={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "100%",
                          backgroundColor:
                            theme.palette.primitive.brand.purple[700],
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CheckIcon
                          sx={{
                            width: "12px",
                            height: "12px",
                            color: theme.palette.neutral.main,
                          }}
                          fontSize="small"
                        />
                      </Box>
                    ) : null
                  }
                >
                  <ListItemButton
                    onClick={() => toggleResource(resource)}
                    disableRipple
                    aria-checked={isSelected}
                    role="checkbox"
                    sx={{
                      minHeight: "48px",
                      pl: "24px",
                      // Room for the absolutely positioned check icon.
                      pr: "48px",
                      gap: "12px",
                      backgroundColor: isSelected
                        ? theme.palette.primitive.brand.purple[25]
                        : "transparent",
                      ":hover": {
                        backgroundColor: theme.palette.neutral["B-005"],
                      },
                    }}
                  >
                    <Avatar
                      url={resource.profile_image}
                      fullname={fullname}
                      variants="sm"
                    />
                    <Typography
                      variant="body-small"
                      title={fullname}
                      sx={{
                        minWidth: 0,
                        textTransform: "capitalize",
                        color: theme.palette.lightBg.high,
                      }}
                      noWrap
                    >
                      {fullname}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Drawer>
    </>
  );
};

const ScopeTab = ({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();

  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      aria-pressed={isActive}
      aria-label={`Scope dashboard to ${label}`}
      onClick={onClick}
      sx={{
        alignItems: "center",
        gap: "6px",
        height: "28px",
        px: "10px",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        color: isActive
          ? theme.palette.lightBg.high
          : theme.palette.lightBg.low,
        backgroundColor: isActive ? theme.palette.neutral.main : "transparent",
        boxShadow: isActive
          ? "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A"
          : "none",
        ":hover": {
          color: theme.palette.lightBg.high,
        },
      }}
    >
      <Typography variant="button-label" color="inherit" noWrap>
        {label}
      </Typography>
      {icon}
    </Stack>
  );
};
